import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { MediaText } from 'editor/src/store/design/types';
import { buildTextStyleElementUpdate, getTextStyle } from 'editor/src/store/design/util/textStyles';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import addNewTextToPageOperation from './addNewTextToPageOperation';
import loadTextStyleFontOperation from './loadTextStyleFontOperation';

import type { TextStyleUpdateResult } from './updateTextStyleOperation';

/**
 * GAPI-61895: clicking a style card with nothing selected adds a text in that style: the
 * regular "Add text" (placement, default colour, selection, edit mode) and then the style,
 * as one undo step (the step "Add text" saves). The new text keeps its centre when the
 * style re-measures it. Resolves `failed` when the style's font could not be loaded.
 */
const addTextInStyleOperation =
  (styleId: string, spreadIndex: number, pageIndex = 0) =>
  async (dispatch: ThunkDispatch, getState: () => RootState): Promise<TextStyleUpdateResult> => {
    const style = getTextStyle(getState().design.designData, styleId);
    if (!style) {
      return 'skipped';
    }
    // Measure the new text in the font it will render in.
    if (!(await dispatch(loadTextStyleFontOperation(style.fontFamily)))) {
      return 'failed';
    }
    const current = getTextStyle(getState().design.designData, styleId);
    if (!current) {
      return 'skipped';
    }

    let result: TextStyleUpdateResult = 'skipped';
    batch(() => {
      const previousSelection = getState().editor.selectedElementUuids;
      dispatch(addNewTextToPageOperation(spreadIndex, pageIndex));
      const state = getState();
      const [uuid] = state.editor.selectedElementUuids;
      if (state.editor.selectedElementUuids === previousSelection || uuid === undefined) {
        return; // nothing was added (no room on the page)
      }
      const elementAddress = getStructureIndexByElementUuid(state.design.designData, uuid);
      const element = elementAddress && getMediaElement(state, elementAddress);
      if (!elementAddress || element?.type !== 'text') {
        return;
      }
      const elementUpdate = buildTextStyleElementUpdate(element, current);
      if (!element.r && elementUpdate.width !== undefined && elementUpdate.height !== undefined) {
        elementUpdate.x = element.x + (element.width - elementUpdate.width) / 2;
        elementUpdate.y = element.y + (element.height - elementUpdate.height) / 2;
      }
      dispatch(updateMediaElementAction({ elementAddress, elementUpdate: elementUpdate as Partial<MediaText> }));
      result = 'applied';
    });
    return result;
  };

export default addTextInStyleOperation;
